import { Fragment, useId } from 'react';
import { f1, linearTicks, scaleY, smoothPath } from './scale';

export type LineSeries = {
  label: string;
  values: number[];
  /** Cor da linha e dos pontos. */
  color?: string;
  /** Cor do degradê da área (sem = sem área). */
  areaColor?: string;
};

export type XTick = { index: number; label: string; /** Deslocamento horizontal do rótulo (bordas). */ dx?: number };

export type LineChartProps = {
  series: LineSeries[];
  /** Nome de cada ponto (dica ao passar o mouse), ex.: "1 mai". */
  pointLabels: string[];
  xTicks?: XTick[];
  /** Rótulos do eixo Y. */
  yFormat?: (v: number) => string;
  /** Texto da dica de cada ponto. */
  pointFormat?: (v: number) => string;
  yTickCount?: number;
  /** Descrição acessível do gráfico. */
  label: string;
  width?: number;
  height?: number;
};

const BRL_AXIS = (v: number) => `R$ ${v.toLocaleString('pt-BR')}`;
const PAD = { left: 66, right: 14, top: 12, bottom: 40 };

/** Gráfico de linha em SVG próprio (sem biblioteca), com o mesmo desenho do pacote. */
export function LineChart({ series, pointLabels, xTicks = [], yFormat = BRL_AXIS, pointFormat = (v) => `R$ ${v}`, yTickCount = 4, label, width = 700, height = 232 }: LineChartProps) {
  const gid = useId().replace(/:/g, '');
  const left = PAD.left;
  const right = width - PAD.right;
  const top = PAD.top;
  const bottom = height - PAD.bottom;
  const all = series.flatMap((s) => s.values);
  const ticks = linearTicks(all.length ? Math.max(...all) : 0, yTickCount);
  const max = ticks[ticks.length - 1];
  const n = Math.max(...series.map((s) => s.values.length), 1);
  const x = (i: number) => (n > 1 ? left + (i * (right - left)) / (n - 1) : left);
  const y = (v: number) => scaleY(v, max, top, bottom);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <defs>
        {series.map((s, si) => s.areaColor && (
          <linearGradient key={si} id={`${gid}-${si}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={s.areaColor} stopOpacity=".28" />
            <stop offset="1" stopColor={s.areaColor} stopOpacity=".02" />
          </linearGradient>
        ))}
      </defs>
      {[...ticks].reverse().map((t) => (
        <Fragment key={t}>
          <line x1={left} x2={right} y1={f1(y(t))} y2={f1(y(t))} stroke="#eef2f8" />
          <text x={left - 12} y={f1(y(t) + 4)} textAnchor="end">{yFormat(t)}</text>
        </Fragment>
      ))}
      {series.map((s, si) => {
        const pts = s.values.map((v, i) => [Number(f1(x(i))), Number(f1(y(v)))] as [number, number]);
        const d = smoothPath(pts);
        const color = s.color ?? '#e0a734';
        return (
          <Fragment key={s.label}>
            {s.areaColor && pts.length > 0 && (
              <path d={`${d} L${f1(pts[pts.length - 1][0])},${f1(bottom)} L${f1(pts[0][0])},${f1(bottom)}Z`} fill={`url(#${gid}-${si})`} />
            )}
            <path d={d} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" />
            {pts.map(([cx, cy], i) => (
              <Fragment key={i}>
                <circle cx={f1(cx)} cy={f1(cy)} r="3.6" fill="#fff" stroke={color} strokeWidth="1.8" />
                <title>{`${pointLabels[i] ?? i + 1}: ${pointFormat(s.values[i])}`}</title>
              </Fragment>
            ))}
          </Fragment>
        );
      })}
      {xTicks.map((t) => (
        <text key={t.index} x={f1(x(t.index) + (t.dx ?? 0))} y={height - 12} textAnchor="middle">{t.label}</text>
      ))}
    </svg>
  );
}
